import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subject, takeUntil } from 'rxjs';
import { SignalRService, SignalRNotification } from '../../core/services/signalr.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

interface ToastItem extends SignalRNotification {
    isVisible: boolean;
    timeoutId?: ReturnType<typeof setTimeout>;
}

@Component({
    selector: 'app-toast',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressBarModule],
    template: `
        <div class="toast-container">
            @for (toast of visibleToasts(); track toast.id) {
                <div
                    class="toast-item"
                    [class.toast-info]="toast.type === 'Info'"
                    [class.toast-success]="toast.type === 'Success'"
                    [class.toast-warning]="toast.type === 'Warning'"
                    [class.toast-error]="toast.type === 'Error'"
                    [class.toast-progress]="toast.type === 'Progress'"
                    [@toastAnimation]="toast.isVisible ? 'visible' : 'hidden'"
                >
                    <div class="toast-content">
                        <mat-icon class="toast-icon">{{ getIcon(toast) }}</mat-icon>
                        <div class="toast-text">
                            <div class="toast-title">{{ toast.title }}</div>
                            <div class="toast-message">{{ toast.message }}</div>
                            @if (toast.type === 'Progress' && toast.progress !== undefined) {
                                <mat-progress-bar
                                    mode="determinate"
                                    [value]="toast.progress"
                                    class="toast-progress-bar">
                                </mat-progress-bar>
                                <div class="toast-progress-text">{{ toast.progressMessage }}</div>
                            }
                        </div>
                        <div class="toast-actions">
                            @if (toast.link) {
                                <button mat-icon-button (click)="navigateTo(toast)">
                                    <mat-icon>open_in_new</mat-icon>
                                </button>
                            }
                            <button mat-icon-button (click)="dismissToast(toast.id)">
                                <mat-icon>close</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: [`
        .toast-container {
            position: fixed;
            top: 80px;
            right: 20px;
            z-index: 9999;
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-width: 400px;
        }

        .toast-item {
            display: flex;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            backdrop-filter: blur(10px);
            min-width: 320px;
        }

        .toast-info {
            background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
            border-left: 4px solid #2196f3;
        }

        .toast-success {
            background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
            border-left: 4px solid #4caf50;
        }

        .toast-warning {
            background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
            border-left: 4px solid #ff9800;
        }

        .toast-error {
            background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
            border-left: 4px solid #f44336;
        }

        .toast-progress {
            background: linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%);
            border-left: 4px solid #3f51b5;
        }

        .toast-content {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            width: 100%;
        }

        .toast-icon {
            flex-shrink: 0;
            margin-top: 2px;
        }

        .toast-info .toast-icon { color: #1976d2; }
        .toast-success .toast-icon { color: #388e3c; }
        .toast-warning .toast-icon { color: #f57c00; }
        .toast-error .toast-icon { color: #d32f2f; }
        .toast-progress .toast-icon { color: #303f9f; }

        .toast-text {
            flex: 1;
            min-width: 0;
        }

        .toast-title {
            font-weight: 600;
            font-size: 14px;
            color: #212121;
            margin-bottom: 4px;
        }

        .toast-message {
            font-size: 13px;
            color: #616161;
            line-height: 1.4;
        }

        .toast-progress-bar {
            margin-top: 8px;
            height: 4px;
            border-radius: 2px;
        }

        .toast-progress-text {
            font-size: 11px;
            color: #757575;
            margin-top: 4px;
        }

        .toast-actions {
            display: flex;
            gap: 4px;
            flex-shrink: 0;
        }

        .toast-actions button {
            width: 28px;
            height: 28px;
            line-height: 28px;
        }

        .toast-actions mat-icon {
            font-size: 18px;
            width: 18px;
            height: 18px;
        }
    `],
    animations: [
        trigger('toastAnimation', [
            state('visible', style({
                opacity: 1,
                transform: 'translateX(0)'
            })),
            state('hidden', style({
                opacity: 0,
                transform: 'translateX(100%)'
            })),
            transition('hidden => visible', animate('200ms ease-out')),
            transition('visible => hidden', animate('150ms ease-in'))
        ])
    ]
})
export class ToastComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private toasts = signal<ToastItem[]>([]);

    visibleToasts = computed(() => this.toasts().filter(t => t.isVisible));

    private readonly AUTO_DISMISS_DELAY = 5000; // 5 seconds
    private readonly PROGRESS_DISMISS_DELAY = 0; // Don't auto-dismiss progress toasts
    private readonly MAX_TOASTS = 5;

    constructor(
        private signalRService: SignalRService,
        private router: Router
    ) {}

    ngOnInit(): void {
        // Subscribe to notifications
        this.signalRService.notification$
            .pipe(takeUntil(this.destroy$))
            .subscribe(notification => {
                this.addToast(notification);
            });

        // Subscribe to progress updates
        this.signalRService.progress$
            .pipe(takeUntil(this.destroy$))
            .subscribe(notification => {
                this.updateOrAddProgressToast(notification);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        // Clear all timeouts
        this.toasts().forEach(toast => {
            if (toast.timeoutId) {
                clearTimeout(toast.timeoutId);
            }
        });
    }

    private addToast(notification: SignalRNotification): void {
        const toast: ToastItem = {
            ...notification,
            isVisible: true
        };

        // Set auto-dismiss timeout
        const delay = notification.type === 'Progress' ? this.PROGRESS_DISMISS_DELAY : this.AUTO_DISMISS_DELAY;
        if (delay > 0) {
            toast.timeoutId = setTimeout(() => this.dismissToast(toast.id), delay);
        }

        // Add to list, remove oldest if exceeds max
        this.toasts.update(toasts => {
            const updated = [toast, ...toasts];
            if (updated.length > this.MAX_TOASTS) {
                const removed = updated.pop();
                if (removed?.timeoutId) {
                    clearTimeout(removed.timeoutId);
                }
            }
            return updated;
        });
    }

    private updateOrAddProgressToast(notification: SignalRNotification): void {
        this.toasts.update(toasts => {
            const existingIndex = toasts.findIndex(t => t.id === notification.id);

            if (existingIndex !== -1) {
                // Update existing toast
                const updated = [...toasts];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    ...notification,
                    isVisible: true
                };

                // If progress is 100%, auto-dismiss after a short delay
                if (notification.progress && notification.progress >= 100) {
                    const toast = updated[existingIndex];
                    if (toast.timeoutId) {
                        clearTimeout(toast.timeoutId);
                    }
                    toast.timeoutId = setTimeout(() => this.dismissToast(toast.id), 2000);
                }

                return updated;
            } else {
                // Add new progress toast
                this.addToast(notification);
                return toasts;
            }
        });
    }

    dismissToast(id: string): void {
        this.toasts.update(toasts => {
            const index = toasts.findIndex(t => t.id === id);
            if (index !== -1) {
                const toast = toasts[index];
                if (toast.timeoutId) {
                    clearTimeout(toast.timeoutId);
                }
                // Mark as not visible first for animation
                const updated = [...toasts];
                updated[index] = { ...updated[index], isVisible: false };

                // Remove after animation
                setTimeout(() => {
                    this.toasts.update(t => t.filter(toast => toast.id !== id));
                }, 150);

                return updated;
            }
            return toasts;
        });
    }

    navigateTo(toast: ToastItem): void {
        if (toast.link) {
            if (toast.useRouter !== false) {
                this.router.navigate([toast.link]);
            } else {
                window.open(toast.link, '_blank');
            }
            this.dismissToast(toast.id);
        }
    }

    getIcon(toast: ToastItem): string {
        if (toast.icon) return toast.icon;

        switch (toast.type) {
            case 'Success': return 'check_circle';
            case 'Warning': return 'warning';
            case 'Error': return 'error';
            case 'Progress': return 'hourglass_empty';
            default: return 'info';
        }
    }
}
